/**
 * EncodingRegistry — per-Sheet catalog of wire-format decoders.
 *
 * Each source row carries a server-declared `metadata.encoding`
 * string (e.g. `"event.v1"`, `"weather.v1"`) identifying the
 * format of its cells. The `NormalizingBackend` adapter consults
 * this registry to convert wire → canonical before cells land
 * in `SourceRowCellData`.
 *
 * Decoders are pure: `(wire) => Option<canonical>`. Both sides are a cell
 * value as the stores hold it — JSON, or a `CellValue` with hydrated fields
 * (an event's `DateTime`s) — because a decoder also runs over values a
 * backend already typed as cells, and must pass a canonical value through.
 * A `null` result means the value couldn't normalize. Every inbound cell
 * path applies the one read rule over it (`decodeRead`, through `readOf`):
 * a wire `null` stays a null cell, a rejected value is left out and counted.
 *
 * If a row has no `encoding` (undefined), cells pass through
 * unchanged — callers are responsible for storing canonical
 * values directly.
 */

import type { Option } from "./option.ts";
import { Tracing } from "@tento-core/tracing";

import type { CellValue } from "./dtype.ts";
import { registerBuiltinEncodings } from "../encodings/builtin.ts";
import type { Json } from "./json.ts";

/** A wire → canonical decoder: `null` rejects the value. */
export type EncodingDecoder = (wire: Json | CellValue) => Option<Json | CellValue>;

/**
 * A sheet's catalog of wire-encoding decoders by name (`event.v1`, `weather.v1`, …), which
 * `NormalizingBackend` consults.
 */
export class EncodingRegistry {
  /** A read cell the decoder rejected (`decodeRead`): it is left out of the store. */
  static readonly REJECTED: unique symbol = Symbol("EncodingRegistry.REJECTED");

  /** Rows `peekRejectedReads` keeps at most; a row counted again moves to the newest, the oldest goes first. */
  static readonly MAX_REJECTED_ROWS = 256;

  readonly #byName = new Map<string, EncodingDecoder>();
  /** Read cells the decoder rejected, per row (`readOf`, `peekRejectedReads`), oldest first, bounded. */
  readonly #rejected = new Map<string, number>();

  /**
   * A fresh registry holding every built-in decoder (`event.v1`, `task.v1`,
   * `weather.v1`, `slot.v1`, `timezone.v1`, `logs.v1`;
   * `registerBuiltinEncodings`) — each call its own, so a caller that
   * registers more never changes another's.
   */
  static withBuiltins(): EncodingRegistry {
    const registry = new EncodingRegistry();
    registerBuiltinEncodings(registry);
    return registry;
  }

  register(encoding: string, decoder: EncodingDecoder): void {
    if (this.#byName.has(encoding)) {
      throw new Error(
        `EncodingRegistry: encoding "${encoding}" is already registered`,
      );
    }
    this.#byName.set(encoding, decoder);
  }

  /**
   * Decode a wire value for the given encoding. Returns the
   * raw value unchanged if `encoding` is undefined or no
   * decoder is registered; returns `null` only if the decoder
   * explicitly rejected the value.
   */
  decode(encoding: Option<string>, wire: Json | CellValue): Option<Json | CellValue> {
    if (encoding == null) return wire;
    const decoder = this.#byName.get(encoding);
    if (!decoder) return wire;
    return decoder(wire);
  }

  /**
   * Exception-safe variant of `decode`. A decoder that throws
   * would otherwise take down an entire fetch batch (1000 good
   * cells lost because one was corrupt) or silently kill an
   * `onCellChange` subscription. Treat throws as rejection —
   * same effect as `decode` returning `null` — and record an
   * `encoding-registry/decoder-threw` breadcrumb so the corruption
   * stays visible.
   *
   * Inbound cells reach it through the read rule (`decodeRead`,
   * `readOf`), which every inbound path applies.
   */
  decodeSafe(encoding: Option<string>, wire: Json | CellValue): Option<Json | CellValue> {
    try {
      return this.decode(encoding, wire);
    } catch (err) {
      Tracing.log("encoding-registry/decoder-threw", {
        encoding: encoding ?? "(none)",
        error: err instanceof Error ? err.message : String(err),
      });
      return null;
    }
  }

  /**
   * An inbound cell value as a store keeps it — the one rule of every path a
   * server cell comes in by (a row's own read, `SourceRow`; the sheet-wide
   * and accessor reads, `RowCellCache.decodeCells`; a write's answer,
   * `RowCellCache.decodeCell`; `NormalizingBackend`; `RawSheet.hydrate`), so
   * a store holds the same cells whichever path answered. A wire `null`
   * stays a null cell (the server sent an empty cell); a value passes
   * `decodeSafe`; a value the decoder rejects is `REJECTED`: the caller
   * leaves it out of the store (it is not a null cell, and a client write
   * never sees or clears it). Pure; a path counts through `readOf`.
   */
  decodeRead(
    encoding: Option<string>,
    wire: Option<Json | CellValue>,
  ): Option<Json | CellValue> | typeof EncodingRegistry.REJECTED {
    if (wire == null || encoding == null) return wire;
    const decoded = this.decodeSafe(encoding, wire);
    return decoded ?? EncodingRegistry.REJECTED;
  }

  /**
   * One read of `row`'s cells under the read rule (`decodeRead`): `decode`
   * each cell, then `end()` counts the read's rejections against the row
   * once (`peekRejectedReads`) and traces them once, with the count and the
   * first rejected cell (`cells/decode-rejected {row, encoding, count,
   * firstCell}`), not once per cell. `row` is the row's one identity on
   * every path: its backend key (the `RowNetworkKey` wire string — the
   * canonical lookup key, else the row id).
   */
  readOf(encoding: Option<string>, row: string): EncodingRegistry.Read {
    let count = 0;
    let firstCell: Option<string> = null;
    return {
      decode: (wire, cell) => {
        const value = this.decodeRead(encoding, wire);
        if (value === EncodingRegistry.REJECTED) {
          count++;
          firstCell ??= String(cell);
        }
        return value;
      },
      end: () => {
        if (count === 0 || encoding == null) return;
        const total = (this.#rejected.get(row) ?? 0) + count;
        this.#rejected.delete(row);
        this.#rejected.set(row, total);
        for (const oldest of this.#rejected.keys()) {
          if (this.#rejected.size <= EncodingRegistry.MAX_REJECTED_ROWS) break;
          this.#rejected.delete(oldest);
        }
        Tracing.log("cells/decode-rejected", { row, encoding, count, firstCell: firstCell ?? "" });
        count = 0;
        firstCell = null;
      },
    };
  }

  /** How many inbound cells the decoder rejected (`readOf`), per row, for the newest `MAX_REJECTED_ROWS` rows. */
  peekRejectedReads(): ReadonlyMap<string, number> {
    return this.#rejected;
  }

  has(encoding: string): boolean {
    return this.#byName.has(encoding);
  }

  size(): number {
    return this.#byName.size;
  }
}

/** Types that go with `EncodingRegistry`. */
export namespace EncodingRegistry {
  /** One read's decode (`EncodingRegistry.readOf`). */
  export interface Read {
    /** A cell's value under the read rule (`decodeRead`); `cell` names it in the trace. */
    decode(wire: Option<Json | CellValue>, cell: { toString(): string }): Option<Json | CellValue> | typeof EncodingRegistry.REJECTED;
    /** Count and trace the read's rejections once (nothing when none). Call it once the read's cells are decoded. */
    end(): void;
  }
}
